import {
  Box,
  Button,
  CircularProgress,
  Divider,
  InputAdornment,
  TextField,
  Tooltip,
  withStyles,
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import { Autocomplete, Skeleton } from '@material-ui/lab';
import { fetchContactById } from '@src/reducers/contactRToolkit';
import {
  cleanShopifyProductDetail,
  fetchShopifyCollections,
  fetchShopifyInventory,
  fetchShopifyShoppingCart,
  removeFilter,
  sendShopifyCatalog,
  setShoppingTabView,
  updateSearchPattern,
  updateSelectedLocationId,
  updateSelectedStoreId,
} from '@src/reducers/shopify';
import { FilterKeys, ShopifyTabsView } from '@src/reducers/shopify/types';
import { isChatComposerDisabled } from '@src/selectors/agentConsole';
import {
  getActiveContactId,
  getIsLoadingProducts,
  getProductInventoryItems,
  getSearchPattern,
  getSelectedFilters,
  getSelectedLocation,
  getSelectedStore,
  getShopifyError,
  getShopifyFetchDetailError,
  getShopifyLocations,
  getShopifyProductsPageInfo,
  getShopifyShoppingCartTotalItems,
  getShopifyStores,
  getShowOrdersHistory,
  getShowProductDetail,
  getShowShoppingCart,
  getTotalInventoryItems,
} from '@src/selectors/shopify';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import FilterWrapper from './Filter';
import FilterTag from './FilterTag';
import OrderByWrapper from './OrderBy';
import OrdersHistoryWrapper from './OrdersHistory';
import ProductWrapper from './Product';
import ProductDetailsWrapper from './ProductDetails';
import ShoppingCartWrapper from './ShoppingCart';
import ShoppingCartCounter from './ShoppingCartCounter';
import {
  NotFoundIcon,
  OrderHistoryIcon,
  SendBlackIcon,
  ShoppingCartIcon,
  ShowMoreIcon,
} from './SvgIcons';
import { NotFoundProduct } from './Utils';

const MAX_AMOUNT_OF_FILTERS_TO_DISPLAY = 3;

const ButtonStyled = withStyles({
  root: {
    'min-width': '55px',
  },
})(Button);

const ShopifyViewer: React.FC = () => {
  const dispatch = useDispatch();
  const products = useSelector(getProductInventoryItems);
  const showProductDetail = useSelector(getShowProductDetail);
  const fetchDetailError = useSelector(getShopifyFetchDetailError);
  const selectedLocation = useSelector(getSelectedLocation);
  const selectedStore = useSelector(getSelectedStore);
  const locations = useSelector(getShopifyLocations);
  const searchPattern = useSelector(getSearchPattern);
  const isLoadingProducts = useSelector(getIsLoadingProducts);
  const selectedFilters = useSelector(getSelectedFilters);
  const showShoppingCart = useSelector(getShowShoppingCart);
  const fetchError = useSelector(getShopifyError);
  const disableSendButtons = useSelector(isChatComposerDisabled);
  const showOrdersHistory = useSelector(getShowOrdersHistory);
  const totalInventoryItems = useSelector(getTotalInventoryItems);
  const pageInfo = useSelector(getShopifyProductsPageInfo);
  const activeContactId = useSelector(getActiveContactId);
  const stores = useSelector(getShopifyStores);
  const totalProductsCounter = useSelector(getShopifyShoppingCartTotalItems);

  const [showAllSelectedFilters, setShowAllSelectedFilters] = useState(false);

  const intl = useIntl();
  const { formatMessage } = intl;

  const productsListRef = useRef<HTMLElement | null>(null);

  const handleMoveScroll = () => {
    if (!productsListRef.current) return;
    const { scrollHeight, offsetHeight, scrollTop } = productsListRef.current;
    const height = scrollHeight - offsetHeight;
    const diff = height - Math.abs(scrollTop);
    const threshold = 200;
    if (diff <= threshold && pageInfo?.hasNextPage) {
      dispatch(fetchShopifyInventory());
    }
  };

  const handleRemove = (key: string, label: string) => {
    dispatch(
      removeFilter({
        key,
        label,
      })
    );
    dispatch(fetchShopifyInventory());
  };

  const selectedFiltersToDisplay = selectedFilters.slice(
    0,
    showAllSelectedFilters
      ? selectedFilters.length
      : MAX_AMOUNT_OF_FILTERS_TO_DISPLAY
  );

  const filtersView = selectedFiltersToDisplay.map(item => {
    if (!item) return null;
    const { key, label } = item;
    let textLabel = `${item.key}: ${item.label}`;
    if (key === FilterKeys.SHOW_ONLY_AVAILABLE_PRODUCTS)
      textLabel = formatMessage({ id: label });

    return (
      <FilterTag
        key={textLabel}
        label={textLabel}
        clickOnRemove={() => handleRemove(key, label)}
      />
    );
  });

  const ErrorOrNotFound = (
    <Box>
      <Box
        display="flex"
        alignItems="center"
        flexDirection="column"
        textAlign="center"
        height="100%"
        paddingTop={10}
      >
        {NotFoundIcon}
        <Box color="text.primary" fontSize={16} paddingTop={2}>
          {fetchError
            ? formatMessage({ id: 'Shopify.fetch.error.text' })
            : formatMessage({ id: 'Shopify.Filter.NotFound.text' })}
        </Box>
      </Box>
    </Box>
  );

  useEffect(() => {
    dispatch(fetchShopifyShoppingCart());
  }, [dispatch, selectedStore]);

  useEffect(() => {
    if (selectedLocation && selectedStore) {
      dispatch(fetchShopifyCollections());
      dispatch(fetchShopifyInventory());
    }
  }, [dispatch, selectedLocation, selectedStore, searchPattern]);

  useEffect(() => {
    if (activeContactId)
      dispatch(fetchContactById({ id: `${activeContactId}` }));
  }, [dispatch, activeContactId]);

  if (fetchDetailError) {
    return (
      <NotFoundProduct
        goBack={formatMessage({
          id: 'Shopify.product.button.goBack.label',
        })}
        message={formatMessage({
          id: 'Shopify.product.notFound.details.label',
        })}
        handleClickOnBack={() => dispatch(cleanShopifyProductDetail())}
      />
    );
  }

  if (showProductDetail) {
    return <ProductDetailsWrapper />;
  }

  if (showShoppingCart) {
    return <ShoppingCartWrapper />;
  }

  if (showOrdersHistory) {
    return <OrdersHistoryWrapper />;
  }

  return (
    <Box
      style={{
        padding: '16px',
      }}
    >
      <Box display="flex" marginBottom={1} style={{ gap: 13 }}>
        <Button
          style={{
            fontSize: '14px',
            lineHeight: '100%',
            height: '32px',
            width: '132px',
          }}
          onClick={() => {
            dispatch(setShoppingTabView(ShopifyTabsView.SHOPPING_CART));
            dispatch(fetchShopifyShoppingCart());
          }}
          disabled={disableSendButtons}
          variant="contained"
          disableElevation
          color="primary"
        >
          <Box
            display="flex"
            flexDirection="row"
            alignItems="center"
            style={{
              gap: '9px',
              ...(disableSendButtons ? { opacity: 0.5 } : {}),
            }}
          >
            <Box>{ShoppingCartIcon}</Box>
            <Box width="min-content">
              {formatMessage({ id: 'Shopify.shoppingCart.see.button.label' })}
            </Box>
            <ShoppingCartCounter totalProductsCounter={totalProductsCounter} />
          </Box>
        </Button>
        <Button
          style={{
            fontSize: '14px',
            lineHeight: '100%',
            height: '32px',
            color: 'inherit',
          }}
          onClick={() => {
            dispatch(sendShopifyCatalog());
          }}
          disabled={disableSendButtons}
          variant="outlined"
          disableElevation
          size="small"
          fullWidth
        >
          <Box
            display="flex"
            flexDirection="row"
            alignItems="center"
            style={{ gap: '9px' }}
          >
            <Box color="text.primary">
              {formatMessage({ id: 'Shopify.Send.Catalog.Button.Label' })}
            </Box>
            <Box
              display="flex"
              color="text.primary"
              style={disableSendButtons ? { opacity: 0.5 } : {}}
            >
              {SendBlackIcon}
            </Box>
          </Box>
        </Button>
      </Box>
      <Box mb={1}>
        {selectedStore?.id ? (
          <Autocomplete
            options={stores}
            disabled={stores.length === 1}
            getOptionLabel={st => st?.store}
            value={stores.find(item => item.id === selectedStore.id)}
            getOptionSelected={(option, value) => option.id === value.id}
            renderInput={params => (
              <TextField
                {...params}
                InputProps={{
                  ...params.InputProps,
                  style: { height: '32px', padding: '0 2px' },
                }}
              />
            )}
            onChange={(_, val) => {
              if (val) dispatch(updateSelectedStoreId(val.id));
            }}
            style={{ width: '100%', padding: 0, margin: 0 }}
          />
        ) : (
          <Skeleton
            animation="wave"
            variant="rect"
            width="100%"
            height={32}
            style={{ borderRadius: '8px' }}
          />
        )}
      </Box>

      <Box display="flex" marginBottom={1} style={{ gap: 20 }}>
        {selectedLocation?.id ? (
          <Autocomplete
            options={locations}
            getOptionLabel={location => location?.name}
            value={selectedLocation}
            getOptionSelected={(option, value) => option.id === value.id}
            renderInput={params => (
              <TextField
                {...params}
                InputProps={{
                  ...params.InputProps,
                  style: { height: '32px', padding: '0 2px' },
                }}
              />
            )}
            onChange={(_, val) => {
              if (val) dispatch(updateSelectedLocationId(val.id));
            }}
            style={{ width: '100%', padding: 0, margin: 0 }}
          />
        ) : (
          <Skeleton
            animation="wave"
            variant="rect"
            width="100%"
            height={32}
            style={{ borderRadius: '8px' }}
          />
        )}

        <Tooltip
          title={formatMessage({
            id: 'Shopify.shoppingCart.ordersHistory.button.tooltip',
          })}
        >
          <ButtonStyled
            style={{
              fontSize: '14px',
              lineHeight: '100%',
              height: '32px',
              color: 'inherit',
            }}
            onClick={() =>
              dispatch(setShoppingTabView(ShopifyTabsView.ORDERS_HISTORY))
            }
            variant="outlined"
            disableElevation
          >
            {OrderHistoryIcon}
          </ButtonStyled>
        </Tooltip>
      </Box>

      <Box
        style={{
          display: 'flex',
          gap: 20,
        }}
        paddingBottom="12px"
      >
        <TextField
          placeholder={formatMessage({
            id: 'Shopyfy.Search.Products.Input.Label',
          })}
          onChange={event => dispatch(updateSearchPattern(event.target.value))}
          value={searchPattern}
          style={{ color: '#A1A1AA' }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <SearchIcon style={{ color: '#A1A1AA' }} />
              </InputAdornment>
            ),
            style: { height: '32px', width: '100%' },
          }}
        />
        <FilterWrapper />
      </Box>

      <Divider style={{ marginBottom: '8px' }} />

      <Box style={{ marginBottom: 14 }}>
        <OrderByWrapper />

        <Box
          textAlign="right"
          fontSize={10}
          paddingTop={0.5}
          color="text.secondary"
          minHeight={15.5}
        >
          {products &&
            products.length > 0 &&
            !isLoadingProducts &&
            totalInventoryItems > 0 &&
            `${formatMessage({
              id: 'Shopify.totalProducts.label',
            })}
          ${totalInventoryItems}`}
        </Box>
      </Box>

      <Box
        display="flex"
        flexWrap="wrap"
        flexDirection="column"
        style={{ gap: 4 }}
      >
        {filtersView}
        {!showAllSelectedFilters &&
          selectedFilters.length > MAX_AMOUNT_OF_FILTERS_TO_DISPLAY && (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="right"
              color="#4C33FF"
              style={{ gap: 12, cursor: 'pointer' }}
              onClick={() => setShowAllSelectedFilters(true)}
            >
              <Box>{formatMessage({ id: 'Shopify.Filter.showMore' })}</Box>
              {ShowMoreIcon}
            </Box>
          )}
      </Box>

      <Box
        height="calc(100vh - 380px)"
        overflow="auto"
        {...{ ref: productsListRef }}
        onScroll={handleMoveScroll}
      >
        <Box marginBottom={8}>
          {products?.map(product => {
            const { id } = product;
            return <ProductWrapper key={id} product={product} />;
          })}
        </Box>

        {products && !products.length && !isLoadingProducts && ErrorOrNotFound}
        {isLoadingProducts && !fetchError && (
          <Box
            display="flex"
            alignItems="center"
            flexDirection="column"
            textAlign="center"
            height="100%"
          >
            <CircularProgress />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ShopifyViewer;
